import React, { useEffect, useMemo, useRef, useState } from "react";
import { Table, Tag, Row, Col, Statistic, Tooltip, Input, Popconfirm, DatePicker, Button } from "antd";
import {
  CrownFilled,
  CrownOutlined,
  StarFilled,
  StarOutlined,
  ExperimentFilled,
  ExperimentOutlined,
  CloseCircleFilled,
  CloseCircleOutlined,
} from "@ant-design/icons";
import LiquidGlass from "./LiquidGlass";
import axios from "axios";

function formatDate(value) {
  if (!value) return "-";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString();
  } catch {
    return String(value);
  }
}

function secondsToHrs(seconds) {
  const s = Number(seconds || 0);
  return (s / 3600).toFixed(2);
}

const AdminDashboard = ({ onOpenUserHistory }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ summary: {}, users: [] });
  const [search, setSearch] = useState("");
  const tableWrapRef = useRef(null);
  const [tableY, setTableY] = useState(480);
  const [statusFilter, setStatusFilter] = useState(["active"]); // default: show active (both admins and members)
  const [lastSessionFilter, setLastSessionFilter] = useState([null, null]); // [startISO, endISO]
  const [rangeStats, setRangeStats] = useState(null);
  const isProdHost = useMemo(() => {
    try {
      return window?.location?.hostname === "simplyride.app";
    } catch {
      return false;
    }
  }, []);

  // Axios instance with token + refresh interceptor (aligned with App.js)
  const axiosInstance = useMemo(() => {
    const instance = axios.create({
      baseURL: "/api",
      headers: { "Content-Type": "application/json" },
    });
    instance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        if (token) config.headers["Authorization"] = `Bearer ${token}`;
        return config;
      },
      (err) => Promise.reject(err)
    );
    instance.interceptors.response.use(
      (response) => response,
      async (err) => {
        const originalRequest = err.config || {};
        if (
          err.response &&
          err.response.status === 401 &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;
          try {
            const refreshToken = localStorage.getItem("refresh_token");
            const refreshResponse = await axios.post("api/refresh_tokens/", {
              refresh_token: refreshToken,
            });
            if (refreshResponse.status === 200) {
              const {
                access_token: newToken,
                refresh_token: newRefreshToken,
              } = refreshResponse.data;
              localStorage.setItem("token", newToken);
              localStorage.setItem("refresh_token", newRefreshToken);
              originalRequest.headers =
                originalRequest.headers || {};
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              return instance(originalRequest);
            }
          } catch (refreshError) {
            // On refresh failure, clear tokens so the app can handle logout upstream
            localStorage.removeItem("token");
            localStorage.removeItem("refresh_token");
          }
        }
        return Promise.reject(err);
      }
    );
    return instance;
  }, []);

  // Simple retry wrapper with exponential backoff for transient errors
  async function requestWithRetry(requestFn, maxRetries = 2, initialDelayMs = 300) {
    let attempt = 0;
    let delay = initialDelayMs;
    // Total attempts = 1 + maxRetries
    for (;;) {
      try {
        return await requestFn();
      } catch (err) {
        attempt += 1;
        const status = err?.response?.status;
        const isNetworkError = !err?.response;
        const isServerError = status >= 500 && status < 600;
        const isTimeout = err?.code === "ECONNABORTED";
        const retriable = isNetworkError || isServerError || isTimeout;
        // Do not retry auth errors; interceptor handles 401
        if (!retriable || attempt > maxRetries) throw err;
        const waitMs = delay;
        await new Promise((r) => setTimeout(r, waitMs));
        delay *= 2; // backoff
      }
    }
  }

  useEffect(() => {
    let isMounted = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const resp = await requestWithRetry(() =>
          axiosInstance.get("/admin/users/")
        );
        const json = resp.data;
        if (isMounted) {
          setData(json);
        }
      } catch (e) {
        if (isMounted) {
          const status = e?.response?.status;
          const detail =
            e?.response?.data?.detail ||
            e?.message ||
            (status ? `HTTP ${status}` : "Failed to load");
          setError(detail);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    load();
    return () => {
      isMounted = false;
    };
  }, [axiosInstance]);

  const filteredUsers = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    let users = data.users || [];
    if (q) {
      users = users.filter((u) => {
        const hay = [u.username, u.full_name, u.email]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
    }
    // Apply grouped filtering: OR within each group (role/status/tier), AND across groups.
    if (Array.isArray(statusFilter) && statusFilter.length > 0) {
      const selected = new Set(statusFilter);
      const roleKeys = ["admin", "member"];
      const lifecycleKeys = ["active", "deleted"];
      const tierKeys = ["premium", "beta_tester"];

      const selectedRoles = roleKeys.filter((k) => selected.has(k));
      const selectedLifecycle = lifecycleKeys.filter((k) => selected.has(k));
      const selectedTier = tierKeys.filter((k) => selected.has(k));

      users = users.filter((u) => {
        const isAdmin = Boolean(u.is_admin);
        const isPremium = Boolean(u.is_premium);
        const isBeta = Boolean(u.is_beta_tester);
        const isDeleted = Boolean(u.deleted_dt);

        const roleOk =
          selectedRoles.length === 0 ||
          selectedRoles.some((k) => (k === "admin" ? isAdmin : !isAdmin));

        const lifecycleOk =
          selectedLifecycle.length === 0 ||
          selectedLifecycle.some((k) => (k === "deleted" ? isDeleted : !isDeleted));

        const tierOk =
          selectedTier.length === 0 ||
          selectedTier.some((k) => {
            if (k === "premium") return isPremium;
            if (k === "beta_tester") return isBeta;
            return true;
          });

        return roleOk && lifecycleOk && tierOk;
      });
    }
    // Apply Last Session date range filter
    const startIso = lastSessionFilter?.[0] || null;
    const endIso = lastSessionFilter?.[1] || null;
    const startMs = startIso ? new Date(startIso).getTime() : null;
    const endMs = endIso ? new Date(endIso).getTime() : null;
    if (startMs || endMs) {
      users = users.filter((u) => {
        const ls = u?.stats?.last_session_start_time;
        if (!ls) return false;
        const ts = new Date(ls).getTime();
        if (!Number.isFinite(ts)) return false;
        if (startMs && ts < startMs) return false;
        if (endMs && ts > endMs) return false;
        return true;
      });
    }
    return users;
  }, [search, statusFilter, lastSessionFilter, data.users]);

  // Map per-user filtered stats for quick lookup
  const rangeUserMap = useMemo(() => {
    const map = new Map();
    try {
      if (rangeStats && Array.isArray(rangeStats.users)) {
        for (const u of rangeStats.users) {
          if (u && u.username) {
            map.set(u.username, {
              sessions: Number(u.sessions || 0),
              duration_seconds: Number(u.duration_seconds || 0),
            });
          }
        }
      }
    } catch {
      // no-op
    }
    return map;
  }, [rangeStats]);

  // Fetch aggregated range stats for visible users when a date range is active
  useEffect(() => {
    const startIso = lastSessionFilter?.[0] || null;
    const endIso = lastSessionFilter?.[1] || null;
    if (!startIso && !endIso) {
      setRangeStats(null);
      return;
    }
    const usernames = (filteredUsers || []).map((u) => u.username).filter(Boolean);
    if (usernames.length === 0) {
      setRangeStats({ summary: { total_sessions: 0, total_duration_seconds: 0 }, users: [] });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await requestWithRetry(() =>
          axiosInstance.post("/admin/users/range_stats/", {
            start_iso: startIso,
            end_iso: endIso,
            usernames,
          })
        );
        if (!cancelled) setRangeStats(resp.data);
      } catch (e) {
        if (!cancelled) setRangeStats(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axiosInstance, lastSessionFilter, filteredUsers]);

  const columns = [
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      render: (val, row) => {
        const chips = [];
        if (row.is_admin) chips.push(<Tag key="admin" color="purple">admin</Tag>);
        if (row.is_premium) chips.push(<Tag key="premium" color="#fadb14">premium</Tag>);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span
              style={{ fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
              onClick={() => {
                try {
                  if (onOpenUserHistory && row?.username) onOpenUserHistory(row.username);
                } catch {}
              }}
              title="View full history"
            >
              {val}
            </span>
            {chips}
          </div>
        );
      },
      sorter: (a, b) => String(a.username || "").localeCompare(String(b.username || "")),
    },
    {
      title: "Name",
      dataIndex: "full_name",
      key: "full_name",
      sorter: (a, b) => String(a.full_name || "").localeCompare(String(b.full_name || "")),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 230,
      responsive: ["lg"],
      render: (val) => (
        <Tooltip title={val}>
          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "inline-block", maxWidth: 220 }}>
            {val || "-"}
          </span>
        </Tooltip>
      ),
      sorter: (a, b) => String(a.email || "").localeCompare(String(b.email || "")),
    },
    {
      title: "Created",
      dataIndex: "created_date",
      key: "created_date",
      render: (v) => formatDate(v),
      sorter: (a, b) => String(a.created_date || "").localeCompare(String(b.created_date || "")),
      defaultSortOrder: "descend",
    },
    {
      title: "Last Login",
      dataIndex: "last_login_ts",
      key: "last_login_ts",
      render: (v) => formatDate(v),
      sorter: (a, b) => String(a.last_login_ts || "").localeCompare(String(b.last_login_ts || "")),
    },
    {
      title: "Last Session",
      key: "last_session",
      render: (_, row) => formatDate(row?.stats?.last_session_start_time),
      sorter: (a, b) => {
        const ta = new Date(a?.stats?.last_session_start_time || 0).getTime();
        const tb = new Date(b?.stats?.last_session_start_time || 0).getTime();
        return ta - tb;
      },
      filteredValue:
        lastSessionFilter?.[0] || lastSessionFilter?.[1]
          ? lastSessionFilter
          : [],
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
        return (
          <div style={{ padding: 8, width: 300 }}>
            <DatePicker.RangePicker
              style={{ width: "100%" }}
              allowEmpty={[true, true]}
              onChange={(dates) => {
                if (!dates || dates.length === 0) {
                  setSelectedKeys([]);
                  return;
                }
                const [d0, d1] = dates;
                const start = d0 ? (() => { const dt = d0.toDate(); dt.setHours(0,0,0,0); return dt.toISOString(); })() : null;
                const end = d1 ? (() => { const dt = d1.toDate(); dt.setHours(23,59,59,999); return dt.toISOString(); })() : null;
                setSelectedKeys([start, end].filter(Boolean));
              }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "flex-end" }}>
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  confirm();
                }}
              >
                Apply
              </Button>
              <Button
                size="small"
                onClick={() => {
                  clearFilters && clearFilters();
                  confirm();
                }}
              >
                Reset
              </Button>
            </div>
          </div>
        );
      },
      filterIcon: (filtered) => (
        <span style={{ color: filtered ? "#1677ff" : undefined }}>📅</span>
      ),
    },
    {
      title: "Logins",
      dataIndex: "login_count",
      key: "login_count",
      width: 90,
      align: "right",
      sorter: (a, b) => Number(a.login_count || 0) - Number(b.login_count || 0),
    },
    {
      title: "Sessions",
      key: "sessions",
      width: 96,
      align: "right",
      render: (_, row) => {
        const hasRange = Boolean(lastSessionFilter?.[0] || lastSessionFilter?.[1]);
        if (hasRange) {
          const m = rangeUserMap.get(row.username);
          return Number(m?.sessions || 0);
        }
        return Number(row?.stats?.sessions || 0);
      },
      sorter: (a, b) => Number(a?.stats?.sessions || 0) - Number(b?.stats?.sessions || 0),
    },
    {
      title: "Total Hours",
      key: "total_hours",
      width: 110,
      align: "right",
      render: (_, row) => {
        const hasRange = Boolean(lastSessionFilter?.[0] || lastSessionFilter?.[1]);
        if (hasRange) {
          const m = rangeUserMap.get(row.username);
          return secondsToHrs(Number(m?.duration_seconds || 0));
        }
        return secondsToHrs(row?.stats?.total_duration_seconds || 0);
      },
      sorter: (a, b) =>
        Number(a?.stats?.total_duration_seconds || 0) - Number(b?.stats?.total_duration_seconds || 0),
    },
    {
      title: "Verified",
      dataIndex: "email_verified",
      key: "email_verified",
      width: 110,
      render: (v, row) => {
        const next = !Boolean(v);
        const label = v ? "verified" : "pending";
        const color = v ? "green" : undefined;
        return (
          <Popconfirm
            title={v ? "Mark email as pending?" : "Mark email as verified?"}
            okText="Yes"
            cancelText="No"
            onConfirm={async () => {
              try {
                await requestWithRetry(() =>
                  axiosInstance.post("/admin/users/verify_email/", {
                    username: row.username,
                    verified: next,
                  })
                );
                // Optimistically update local state
                setData((prev) => {
                  const prevSummary = prev?.summary || {};
                  const users = (prev?.users || []).map((u) =>
                    u.username === row.username ? { ...u, email_verified: next } : u
                  );
                  const delta = next ? 1 : -1;
                  const summary = {
                    ...prevSummary,
                    verified_emails: Math.max(
                      0,
                      (prevSummary.verified_emails || 0) + delta
                    ),
                    unverified_emails: Math.max(
                      0,
                      (prevSummary.unverified_emails || 0) - delta
                    ),
                  };
                  return { ...prev, users, summary };
                });
              } catch (e) {
                const status = e?.response?.status;
                const detail =
                  e?.response?.data?.detail ||
                  e?.message ||
                  (status ? `HTTP ${status}` : "Failed to update");
                setError(detail);
              }
            }}
          >
            <Tag color={color} style={{ cursor: "pointer" }}>
              {label}
            </Tag>
          </Popconfirm>
        );
      },
      filters: [
        { text: "Verified", value: true },
        { text: "Pending", value: false },
      ],
      onFilter: (value, record) => Boolean(record.email_verified) === value,
    },
    {
      title: "Strava",
      dataIndex: "strava_connected",
      key: "strava_connected",
      width: 100,
      render: (v) => (v ? <Tag color="orange">linked</Tag> : <Tag>none</Tag>),
      filters: [
        { text: "Linked", value: true },
        { text: "None", value: false },
      ],
      onFilter: (value, record) => Boolean(record.strava_connected) === value,
    },
    {
      title: "Usage",
      key: "usage",
      width: 200,
      render: (_, row) => {
        const usage = row?.usage?.total;
        if (!usage || Object.keys(usage).length === 0) {
          return <span style={{ color: "#999" }}>No data</span>;
        }
        const entries = Object.entries(usage);
        return (
          <div style={{ fontSize: "12px", lineHeight: "1.4" }}>
            {entries.slice(0, 3).map(([key, value]) => (
              <div key={key} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                <span style={{ fontWeight: 500 }}>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</span>{" "}
                <span>{typeof value === 'number' ? value.toLocaleString() : String(value)}</span>
              </div>
            ))}
            {entries.length > 3 && (
              <div style={{ color: "#666", fontStyle: "italic" }}>
                +{entries.length - 3} more
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 160,
      filters: [
        { text: "Admin", value: "admin" },
        { text: "Premium", value: "premium" },
        { text: "Beta Tester", value: "beta_tester" },
        { text: "Member", value: "member" },
        { text: "Deleted", value: "deleted" },
        { text: "Active", value: "active" },
      ],
      filterMultiple: true,
      filteredValue: statusFilter,
      render: (_, row) => {
        const isDeleted = Boolean(row?.deleted_dt);
        const isAdmin = Boolean(row?.is_admin);
        const isPremium = Boolean(row?.is_premium);
        const isBeta = Boolean(row?.is_beta_tester);
        const nextDelete = !isDeleted;
        const nextAdmin = !isAdmin;
        const nextPremium = !isPremium;
        const nextBeta = !isBeta;
        return (
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <Popconfirm
              title={isAdmin ? "Remove admin rights?" : "Grant admin rights?"}
              okText="Yes"
              cancelText="No"
              onConfirm={async () => {
                try {
                  await requestWithRetry(() =>
                    axiosInstance.post("/admin/users/set_admin/", {
                      username: row.username,
                      is_admin: nextAdmin,
                    })
                  );
                  setData((prev) => {
                    const users = (prev?.users || []).map((u) =>
                      u.username === row.username ? { ...u, is_admin: nextAdmin } : u
                    );
                    const summary = { ...(prev?.summary || {}) };
                    if (nextAdmin) {
                      summary.admins = (summary.admins || 0) + 1;
                    } else {
                      summary.admins = Math.max(0, (summary.admins || 0) - 1);
                    }
                    return { ...prev, users, summary };
                  });
                } catch (e) {
                  const status = e?.response?.status;
                  const detail =
                    e?.response?.data?.detail ||
                    e?.message ||
                    (status ? `HTTP ${status}` : "Failed to update role");
                  setError(detail);
                }
              }}
            >
              <span role="button" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }} title={isAdmin ? "Admin (click to revoke)" : "Not admin (click to grant)"}>
                {isAdmin ? (
                  <CrownFilled style={{ color: "#faad14", fontSize: 18 }} />
                ) : (
                  <CrownOutlined style={{ color: "rgba(0,0,0,0.45)", fontSize: 18 }} />
                )}
              </span>
            </Popconfirm>
            <Popconfirm
              title={isPremium ? "Remove premium?" : "Grant premium?"}
              okText="Yes"
              cancelText="No"
              onConfirm={async () => {
                try {
                  await requestWithRetry(() =>
                    axiosInstance.post("/admin/users/set_premium/", {
                      username: row.username,
                      is_premium: nextPremium,
                    })
                  );
                  setData((prev) => {
                    const users = (prev?.users || []).map((u) =>
                      u.username === row.username ? { ...u, is_premium: nextPremium } : u
                    );
                    return { ...prev, users };
                  });
                } catch (e) {
                  const status = e?.response?.status;
                  const detail =
                    e?.response?.data?.detail ||
                    e?.message ||
                    (status ? `HTTP ${status}` : "Failed to update premium");
                  setError(detail);
                }
              }}
            >
              <span
                role="button"
                style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}
                title={isPremium ? "Premium (click to revoke)" : "Not premium (click to grant)"}
              >
                {isPremium ? (
                  <StarFilled style={{ color: "#fadb14", fontSize: 18 }} />
                ) : (
                  <StarOutlined style={{ color: "rgba(0,0,0,0.45)", fontSize: 18 }} />
                )}
              </span>
            </Popconfirm>
            <Popconfirm
              title={isBeta ? "Remove beta tester?" : "Grant beta tester?"}
              okText="Yes"
              cancelText="No"
              onConfirm={async () => {
                try {
                  await requestWithRetry(() =>
                    axiosInstance.post("/admin/users/set_beta_tester/", {
                      username: row.username,
                      is_beta_tester: nextBeta,
                    })
                  );
                  setData((prev) => {
                    const prevSummary = prev?.summary || {};
                    const users = (prev?.users || []).map((u) =>
                      u.username === row.username ? { ...u, is_beta_tester: nextBeta } : u
                    );
                    // Optimistically update header list as well (no refresh needed)
                    const list0 = Array.isArray(prevSummary.beta_tester_usernames)
                      ? prevSummary.beta_tester_usernames.filter(Boolean).map(String)
                      : [];
                    const uname = String(row.username || "").trim();
                    let nextList = list0;
                    if (uname) {
                      if (nextBeta) nextList = Array.from(new Set([...list0, uname]));
                      else nextList = list0.filter((x) => String(x) !== uname);
                      nextList = nextList.slice().sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
                    }
                    const summary = {
                      ...prevSummary,
                      beta_tester_usernames: nextList,
                      beta_tester_count: nextList.length,
                    };
                    return { ...prev, users, summary };
                  });
                } catch (e) {
                  const status = e?.response?.status;
                  const detail =
                    e?.response?.data?.detail ||
                    e?.message ||
                    (status ? `HTTP ${status}` : "Failed to update beta tester");
                  setError(detail);
                }
              }}
            >
              <span
                role="button"
                style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}
                title={isBeta ? "Beta tester (click to revoke)" : "Not beta tester (click to grant)"}
              >
                {isBeta ? (
                  <ExperimentFilled style={{ color: "#1677ff", fontSize: 18 }} />
                ) : (
                  <ExperimentOutlined style={{ color: "rgba(0,0,0,0.45)", fontSize: 18 }} />
                )}
              </span>
            </Popconfirm>
            <Popconfirm
              title={isDeleted ? "Restore user?" : "Soft delete user?"}
              okText="Yes"
              cancelText="No"
              onConfirm={async () => {
                try {
                  await requestWithRetry(() =>
                    axiosInstance.post("/admin/users/delete/", {
                      username: row.username,
                      delete: nextDelete,
                    })
                  );
                  setData((prev) => {
                    const users = (prev?.users || []).map((u) =>
                      u.username === row.username
                        ? {
                            ...u,
                            deleted_dt: nextDelete ? new Date().toISOString() : null,
                          }
                        : u
                    );
                    return { ...prev, users };
                  });
                } catch (e) {
                  const status = e?.response?.status;
                  const detail =
                    e?.response?.data?.detail ||
                    e?.message ||
                    (status ? `HTTP ${status}` : "Failed to update user");
                  setError(detail);
                }
              }}
            >
              <span role="button" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }} title={isDeleted ? "Deleted (click to restore)" : "Active (click to delete)"}>
                {isDeleted ? (
                  <CloseCircleFilled style={{ color: "#b00020", fontSize: 18 }} />
                ) : (
                  <CloseCircleOutlined style={{ color: "rgba(0,0,0,0.45)", fontSize: 18 }} />
                )}
              </span>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  // Dynamically size the table scroll area to fill remaining viewport
  useEffect(() => {
    const updateHeights = () => {
      try {
        if (!tableWrapRef.current) return;
        const rect = tableWrapRef.current.getBoundingClientRect();
        const bottomMargin = 6; // reduce margin to use more viewport height
        const available = Math.max(240, Math.floor(window.innerHeight - rect.top - bottomMargin));
        setTableY(available);
      } catch {
        // no-op
      }
    };
    updateHeights();
    window.addEventListener("resize", updateHeights);
    return () => window.removeEventListener("resize", updateHeights);
  }, []);

  return (
    <div style={{ marginTop: 12 }}>
      {/* Local (scoped) liquid-glass enhancements for the table */}
      <style>{`
        .glass-table .ant-table {
          background: transparent !important;
        }
        .glass-table .ant-table-container {
          background: rgba(255, 255, 255, 0.10);
          border: 1px solid rgba(0, 0, 0, 0.08);
          border-radius: var(--glass-radius);
          backdrop-filter: saturate(160%) blur(12px);
          -webkit-backdrop-filter: saturate(160%) blur(12px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        }
        .glass-table .ant-table-thead > tr > th {
          background: rgba(255, 255, 255, 0.18) !important;
          color: rgba(15, 23, 42, 0.85);
          font-weight: 600;
          border-bottom: 1px solid rgba(0, 0, 0, 0.06);
          font-size: 14px;
          padding: 14px 16px;
        }
        .glass-table .ant-table-tbody > tr > td {
          background: transparent;
          border-bottom: 1px solid rgba(0, 0, 0, 0.06);
          font-size: 14px;
          padding: 12px 16px;
        }
        .glass-table .ant-table-tbody > tr:hover > td {
          background: rgba(255, 255, 255, 0.16) !important;
        }
        .glass-table .ant-pagination {
          margin: 12px !important;
        }
      `}</style>
      <LiquidGlass className="liquid-glass" style={{ padding: 16, margin: "0 auto", maxWidth: 1480 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <h2 style={{ margin: 0, color: "#0f172a" }}>Admin • User Management</h2>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Input.Search
            allowClear
            placeholder="Search by username, name, email"
            onChange={(e) => setSearch(e.target.value)}
              style={{ width: 360, maxWidth: "100%" }}
          />
          </div>
        </div>

        {/* Beta tester env flag + list */}
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
          {!isProdHost ? (
            <div>
              <Tag color="warning">
                You're not using production env so env BETA_TESTER_REQUEST_ACTIVATE might be different, only production one is relevant
              </Tag>
            </div>
          ) : null}

          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, color: "rgba(15, 23, 42, 0.85)" }}>BETA_TESTER_REQUEST_ACTIVATE:</span>
            {data?.summary?.beta_tester_request_activate ? (
              <Tag color="green">enabled</Tag>
            ) : (
              <Tag>disabled</Tag>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, color: "rgba(15, 23, 42, 0.85)" }}>Beta testers:</span>
            <Tooltip
              title={
                Array.isArray(data?.summary?.beta_tester_usernames) && data.summary.beta_tester_usernames.length > 0
                  ? data.summary.beta_tester_usernames.join(", ")
                  : "None"
              }
            >
              <span style={{ color: "rgba(15, 23, 42, 0.8)" }}>
                {Array.isArray(data?.summary?.beta_tester_usernames) && data.summary.beta_tester_usernames.length > 0
                  ? `${data.summary.beta_tester_usernames.slice(0, 8).join(", ")}${data.summary.beta_tester_usernames.length > 8 ? ` +${data.summary.beta_tester_usernames.length - 8} more` : ""}`
                  : "None"}
              </span>
            </Tooltip>
          </div>
          </div>
        </div>

        <Row gutter={[16, 12]} style={{ marginTop: 8 }}>
          <Col xs={12} sm={8} md={6}>
            <Statistic title="Total Users" value={data?.summary?.total_users || 0} />
          </Col>
          <Col xs={12} sm={8} md={6}>
            {Boolean(lastSessionFilter?.[0] || lastSessionFilter?.[1]) && rangeStats?.summary ? (
              <Statistic title="Sessions (filtered)" value={Number(rangeStats.summary.total_sessions || 0)} />
            ) : (
              <Statistic title="Admins" value={data?.summary?.admins || 0} />
            )}
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Statistic title="Verified Emails" value={data?.summary?.verified_emails || 0} />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Statistic
              title={Boolean(lastSessionFilter?.[0] || lastSessionFilter?.[1]) ? "Total Hours (filtered)" : "Total Hours"}
              value={
                Boolean(lastSessionFilter?.[0] || lastSessionFilter?.[1]) && rangeStats?.summary
                  ? secondsToHrs(Number(rangeStats.summary.total_duration_seconds || 0))
                  : secondsToHrs(data?.summary?.total_duration_seconds || 0)
              }
            />
          </Col>
        </Row>
      </LiquidGlass>

      <LiquidGlass
        className="liquid-glass"
        style={{ marginTop: 12, padding: 0, overflow: "hidden", maxWidth: 1480, marginLeft: "auto", marginRight: "auto" }}
        ref={tableWrapRef}
      >
        <Table
          className="glass-table"
          rowKey={(r) => r.username || `${r.email}-${r.created_date}`}
          dataSource={filteredUsers}
          columns={columns}
          loading={loading}
          pagination={false}
          size="large"
          sticky
          scroll={{ x: 1900, y: tableY }}
          onChange={(_, filters) => {
            const keys = (filters && filters.status) || [];
            setStatusFilter(Array.isArray(keys) ? keys : []);
            const ls = (filters && filters.last_session) || [];
            if (Array.isArray(ls) && ls.length > 0) {
              const start = ls[0] || null;
              const end = ls[1] || null;
              setLastSessionFilter([start, end]);
            } else {
              setLastSessionFilter([null, null]);
            }
          }}
          locale={{
            emptyText: error ? `Error: ${error}` : "No users",
          }}
          style={{ borderRadius: "var(--glass-radius)" }}
          summary={() => {
            try {
              const totalUsers = (filteredUsers || []).length;
              const totalLogins = (filteredUsers || []).reduce(
                (sum, u) => sum + Number(u?.login_count || 0),
                0
              );
              const hasRange = Boolean(lastSessionFilter?.[0] || lastSessionFilter?.[1]);
              let totalSessions;
              let totalHours;
              if (hasRange && rangeStats?.summary) {
                totalSessions = Number(rangeStats.summary.total_sessions || 0);
                const seconds = Number(rangeStats.summary.total_duration_seconds || 0);
                totalHours = (seconds / 3600).toFixed(2);
              } else {
                const totalSeconds = (filteredUsers || []).reduce(
                  (sum, u) => sum + Number(u?.stats?.total_duration_seconds || 0),
                  0
                );
                totalSessions = (filteredUsers || []).reduce(
                  (sum, u) => sum + Number(u?.stats?.sessions || 0),
                  0
                );
                totalHours = (totalSeconds / 3600).toFixed(2);
              }
              const Summary = Table.Summary;
              const colCount = (Array.isArray(columns) ? columns.length : 0) || 0;
              return (
                <Summary.Row>
                  {Array.from({ length: colCount }).map((_, idx) => {
                    // Find indices by column keys so ordering changes don't break summary alignment
                    const loginIdx = columns.findIndex((c) => c.key === "login_count");
                    const sessionsIdx = columns.findIndex((c) => c.key === "sessions");
                    const hoursIdx = columns.findIndex((c) => c.key === "total_hours");
                    return (
                      <Summary.Cell index={idx} key={idx}>
                        {idx === 0 ? <strong>Total ({totalUsers})</strong> : null}
                        {idx === loginIdx ? <strong>{totalLogins}</strong> : null}
                        {idx === sessionsIdx ? <strong>{totalSessions}</strong> : null}
                        {idx === hoursIdx ? <strong>{totalHours}</strong> : null}
                      </Summary.Cell>
                    );
                  })}
                </Summary.Row>
              );
            } catch {
              return null;
            }
          }}
        />
      </LiquidGlass>
    </div>
  );
};

export default AdminDashboard;


